// File: src/components/OmniCine/Timeline/Ranges/RangeOverlay.tsx
//
// RangeOverlay — visual ranges overlay (below ruler).
// Contract goals (A3-safe):
// - Timeline-space rendering only (seconds -> px via pxPerSec)
// - Does not fabricate state
// - Root is pointer-events-none; only specific affordances are interactive
//
// Phase N2 (Option 2):
// - Lane-specific “envelope bands” (attention / emotion / overload / safety)
// - If a range has trackId and kind is an envelope kind, it is clipped to that lane’s vertical bounds
// - Uses lane DOM anchors injected by TimelineShell wrappers:
//     data-omnicine-track="1" data-track-id="<id>" data-track-kind="<kind>"
//
// Locked behaviors in this version:
// - ✅ Timeline-absolute X coordinates (x = sec * pxPerSec) to match laneWidthPx layout
// - ✅ Drag/resize uses pointer→timelineSec mapping (no viewport/scroll frame drift)
// - ✅ Scope lane-anchor queries to nearest timeline shell (prevents cross-instance contamination)
// - ✅ No lane flash: lane-scoped envelope bands do not render until lane rect exists
// - ✅ Phase strip rendering (phase:setup|rising|peak|resolve) as thin band under ruler
// - ✅ Snap-aware + clamped + MIN_RANGE_LEN enforced; rAF-throttled dispatch
// - ✅ Never touches/overwrites window.__oc_debug
//
// Notes:
// - Conservative visuals; minimal DOM reads; rAF-throttled measurements and dispatch.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { snapTime as snapTimelineTime } from "@/lib/omnicine/snapTime";

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

type RangeKind =
  | "selection"
  | "loop"
  | "safe" // alias
  | "safety" // canonical
  | "danger" // legacy/meta; kept as alias
  | "attention"
  | "emotion"
  | "overload"
  | "music"
  | "phase"
  | string;

export type TimelineRange = {
  id: string;
  startSec: number;
  endSec: number;
  kind?: RangeKind;
  label?: string;
  trackId?: string;
  tag?: string;
};

type Props = {
  ranges: TimelineRange[];
  pxPerSec: number;
  viewportStartSec: number;
  viewportEndSec: number;

  selectedRangeId?: string | null;
  onSelect?: (id: string) => void;

  filter?: (r: TimelineRange) => boolean;

  durationSec?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  snap?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch?: any;
};

type LaneRect = { top: number; height: number };

/* ------------------------------------------------------------------ */
/* Constants                                                          */
/* ------------------------------------------------------------------ */

const TRACK_SELECTOR = `[data-omnicine-track="1"][data-track-id]`;
const ENVELOPE_KINDS = new Set(["attention", "emotion", "overload", "safety"] as const);

const MIN_RANGE_LEN = 0.05; // seconds (A3-safe minimal)

// Phase labels supported by TimelineShell phase emitter
type NarrativePhase = "setup" | "rising" | "peak" | "resolve";
const PHASE_PREFIX = "phase:" as const;

// Thin phase strip (under ruler)
const PHASE_STRIP_TOP = 6;
const PHASE_STRIP_H = 22;

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function normalizeKind(k?: RangeKind): string {
  const s = String(k ?? "selection").toLowerCase().trim();
  if (s === "safe") return "safety";
  if (s === "danger") return "overload";
  return s;
}

function parsePhaseLabel(label?: string | null): NarrativePhase | null {
  const s = String(label ?? "").trim().toLowerCase();
  if (!s.startsWith(PHASE_PREFIX)) return null;
  const p = s.slice(PHASE_PREFIX.length) as NarrativePhase;
  return p === "setup" || p === "rising" || p === "peak" || p === "resolve" ? p : null;
}

function isPhaseRange(r: TimelineRange): boolean {
  const kindNorm = normalizeKind(r.kind);
  if (kindNorm === "phase") return true;
  return parsePhaseLabel(r.label) != null;
}

function useIsDark(): boolean {
  const [dark, setDark] = useState<boolean>(() =>
    typeof document !== "undefined" ? document.documentElement.classList.contains("dark") : false,
  );

  useEffect(() => {
    if (typeof document === "undefined") return;

    const obs = new MutationObserver(() => {
      setDark(document.documentElement.classList.contains("dark"));
    });

    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  return dark;
}

function colorFor(kind?: RangeKind) {
  switch (normalizeKind(kind)) {
    case "selection":
    case "loop":
      return { fill: "rgba(59,130,246,0.10)", stroke: "rgba(59,130,246,0.55)" };

    case "safety":
      return { fill: "rgba(34,197,94,0.10)", stroke: "rgba(34,197,94,0.55)" };

    case "attention":
      return { fill: "rgba(250,204,21,0.10)", stroke: "rgba(250,204,21,0.60)" };

    case "emotion":
      return { fill: "rgba(168,85,247,0.10)", stroke: "rgba(168,85,247,0.60)" };

    case "overload":
      return { fill: "rgba(245,158,11,0.12)", stroke: "rgba(245,158,11,0.60)" };

    case "music":
      return { fill: "rgba(56,189,248,0.10)", stroke: "rgba(56,189,248,0.55)" };

    default:
      return { fill: "rgba(148,163,184,0.10)", stroke: "rgba(148,163,184,0.50)" };
  }
}

function phaseColors(p: NarrativePhase) {
  switch (p) {
    case "setup":
      return { fill: "rgba(34,211,238,0.10)", stroke: "rgba(34,211,238,0.40)" };
    case "rising":
      return { fill: "rgba(250,204,21,0.10)", stroke: "rgba(250,204,21,0.42)" };
    case "peak":
      return { fill: "rgba(244,63,94,0.10)", stroke: "rgba(244,63,94,0.42)" };
    case "resolve":
      return { fill: "rgba(34,197,94,0.10)", stroke: "rgba(34,197,94,0.40)" };
  }
}

function isSnapEnabled(snap: any): boolean {
  return !!snap?.enabled;
}

function snapTime(t: number, snap: any): number {
  return snapTimelineTime(t, {
    enabled: !!snap?.enabled,
    mode: snap?.mode === "frame" || snap?.mode === "second" || snap?.mode === "marker" || snap?.mode === "beat" ? snap.mode : "second",
    fps: Number.isFinite(Number(snap?.fps)) ? clamp(Number(snap.fps), 1, 240) : 30,
    bpm: Number.isFinite(Number(snap?.bpm)) ? Number(snap.bpm) : undefined,
    beatDiv: Number.isFinite(Number(snap?.beatDiv)) ? Number(snap.beatDiv) : undefined,
    markers: Array.isArray(snap?.markers) ? snap.markers : undefined,
  });
}

/* ------------------------------------------------------------------ */
/* Drag state                                                         */
/* ------------------------------------------------------------------ */

type DragMode = "move" | "resizeStart" | "resizeEnd";

type DragState = {
  rangeId: string;
  mode: DragMode;

  // values at pointerdown
  start0: number;
  end0: number;

  // pointer start (kept for diagnostics; not used for timeline mapping)
  clientX0: number;

  // move-mode: keep grab point stable so range doesn't jump
  grabOffsetSec: number;

  // guardrails
  durationSec: number;
};

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */

export default function RangeOverlay({
  ranges,
  pxPerSec,
  viewportStartSec,
  viewportEndSec,
  selectedRangeId,
  onSelect,
  filter,
  durationSec: durationSecProp,
  snap,
  dispatch,
}: Props): JSX.Element {
  const isDark = useIsDark();

  const rootRef = useRef<HTMLDivElement>(null);
  const [laneRects, setLaneRects] = useState<Record<string, LaneRect>>({});

  const dragRef = useRef<DragState | null>(null);
  const rafDispatchRef = useRef<number>(0);

  /* ---------------------------------------------------------- */
  /* Duration + overlay width                                   */
  /* ---------------------------------------------------------- */

  const durationSec = useMemo(() => {
    const d =
      Number.isFinite(durationSecProp) && (durationSecProp as number) > 0
        ? (durationSecProp as number)
        : Math.max(0, viewportEndSec);
    return d;
  }, [durationSecProp, viewportEndSec]);

  const overlayWidthPx = useMemo(() => {
    const pps = Number.isFinite(pxPerSec) && pxPerSec > 0 ? pxPerSec : 100;
    return Math.max(1, durationSec * pps);
  }, [durationSec, pxPerSec]);

  /* ---------------------------------------------------------- */
  /* Pointer → timeline sec mapping                              */
  /* ---------------------------------------------------------- */
  const pointerToSec = useCallback(
    (clientX: number): number => {
      const root = rootRef.current;
      if (!root) return 0;

      // IMPORTANT:
      // This overlay is inside the horizontally scrolled content. When scrolled,
      // root.getBoundingClientRect().left becomes negative, so (clientX - left)
      // already includes scroll offset. Do NOT add scrollLeft here.
      const rr = root.getBoundingClientRect();
      const pps = Math.max(1e-6, pxPerSec);
      const xPx = clientX - rr.left;
      return xPx / pps;
    },
    [pxPerSec],
  );

  /* ---------------------------------------------------------- */
  /* Lane measurement (Option 2)                                 */
  /* ---------------------------------------------------------- */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    let raf = 0;

    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const rr = root.getBoundingClientRect();
        if (rr.height <= 0) return;

        // ✅ Scope anchor queries to THIS timeline shell
        const shell =
          root.closest(`[data-omnicine-timeline-shell="1"]`) ?? (root.parentElement as HTMLElement | null);

        const nodes = shell
          ? shell.querySelectorAll<HTMLElement>(TRACK_SELECTOR)
          : document.querySelectorAll<HTMLElement>(TRACK_SELECTOR);

        const next: Record<string, LaneRect> = {};

        nodes.forEach((el) => {
          const id = el.getAttribute("data-track-id");
          if (!id) return;

          const r = el.getBoundingClientRect();
          const top = r.top - rr.top;
          const height = r.height;

          if (!Number.isFinite(top) || !Number.isFinite(height) || height <= 0) return;
          next[id] = { top, height };
        });

        setLaneRects(next);
      });
    };

    measure();

    const ro = new ResizeObserver(() => measure());
    ro.observe(root);

    const onResize = () => measure();
    window.addEventListener("resize", onResize);

    const onAnyScroll = () => measure();
    window.addEventListener("scroll", onAnyScroll, true);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onAnyScroll, true);
    };
  }, []);

  /* ---------------------------------------------------------- */
  /* Visible ranges (viewport intersection for perf only)        */
  /* ---------------------------------------------------------- */
  const visible = useMemo(() => {
    const start = viewportStartSec;
    const end = viewportEndSec;
    const fn = filter ?? (() => true);

    return (ranges ?? []).filter((r) => {
      if (!r) return false;
      if (!fn(r)) return false;

      const a = Math.min(r.startSec, r.endSec);
      const b = Math.max(r.startSec, r.endSec);
      return b >= start && a <= end;
    });
  }, [ranges, viewportStartSec, viewportEndSec, filter]);

  /* ---------------------------------------------------------- */
  /* Interaction: pointer move/up (global capture)               */
  /* ---------------------------------------------------------- */
  useEffect(() => {
    if (!dispatch) return;

    const onMove = (e: PointerEvent) => {
      const ds = dragRef.current;
      if (!ds) return;

      const t = pointerToSec(e.clientX);

      let start = ds.start0;
      let end = ds.end0;

      if (ds.mode === "move") {
        const len = Math.max(MIN_RANGE_LEN, ds.end0 - ds.start0);
        const s = t - ds.grabOffsetSec;
        start = clamp(s, 0, Math.max(0, ds.durationSec - len));
        end = clamp(start + len, start + MIN_RANGE_LEN, ds.durationSec);
      } else if (ds.mode === "resizeStart") {
        start = clamp(t, 0, Math.min(ds.end0 - MIN_RANGE_LEN, ds.durationSec));
        end = ds.end0;
      } else if (ds.mode === "resizeEnd") {
        start = ds.start0;
        end = clamp(t, Math.max(0, ds.start0 + MIN_RANGE_LEN), ds.durationSec);
      }

      // Snap after clamps
      start = snapTime(start, snap);
      end = snapTime(end, snap);

      // Re-enforce invariant after snap
      if (end - start < MIN_RANGE_LEN) {
        if (ds.mode === "resizeStart") start = end - MIN_RANGE_LEN;
        else end = start + MIN_RANGE_LEN;
      }

      start = clamp(start, 0, ds.durationSec);
      end = clamp(end, 0, ds.durationSec);
      if (end <= start) end = Math.min(ds.durationSec, start + MIN_RANGE_LEN);

      cancelAnimationFrame(rafDispatchRef.current);
      rafDispatchRef.current = requestAnimationFrame(() => {
        dispatch(
          {
            type: "range/update",
            payload: { rangeId: ds.rangeId, startSec: start, endSec: end },
            meta: { source: "user", note: `range:${ds.mode}` },
          } as any,
        );
      });
    };

    const endDrag = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      cancelAnimationFrame(rafDispatchRef.current);
      rafDispatchRef.current = 0;
    };

    const onUp = () => endDrag();
    const onCancel = () => endDrag();

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      endDrag();
    };
  }, [dispatch, pointerToSec, snap]);

  /* ---------------------------------------------------------- */
  /* Start drag helper                                           */
  /* ---------------------------------------------------------- */
  const beginDrag = useCallback(
    (e: React.PointerEvent, r: TimelineRange, mode: DragMode) => {
      if (!dispatch) return;

      const kindNorm = normalizeKind(r.kind);
      const isEnvelope = ENVELOPE_KINDS.has(kindNorm as any);
      const isPhase = isPhaseRange(r);

      // Only envelope + phase are editable here (prevents accidental UI takeover)
      if (!isEnvelope && !isPhase) return;

      const a = clamp(Math.min(r.startSec, r.endSec), 0, durationSec);
      const b = clamp(Math.max(r.startSec, r.endSec), 0, durationSec);

      const t0 = pointerToSec(e.clientX);
      const grabOffsetSec = mode === "move" ? t0 - a : 0;

      dragRef.current = {
        rangeId: r.id,
        mode,
        start0: a,
        end0: b,
        clientX0: e.clientX,
        grabOffsetSec,
        durationSec,
      };

      onSelect?.(r.id);

      try {
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      } catch {
        // ignore
      }

      e.preventDefault();
      e.stopPropagation();
    },
    [dispatch, durationSec, onSelect, pointerToSec],
  );

  /* ---------------------------------------------------------- */
  /* Render                                                      */
  /* ---------------------------------------------------------- */

  const innerLine = isDark ? "rgba(255,255,255,0.35)" : "rgba(2,6,23,0.30)";

  return (
    <div
      ref={rootRef}
      className="pointer-events-none absolute top-0 bottom-0 left-0 z-20"
      style={{ width: overlayWidthPx }}
    >
      {visible.map((r) => {
        const kindNorm = normalizeKind(r.kind);
        const isEnvelope = ENVELOPE_KINDS.has(kindNorm as any);
        const isPhase = isPhaseRange(r);

        const a0 = Math.min(r.startSec, r.endSec);
        const b0 = Math.max(r.startSec, r.endSec);
        const a = clamp(a0, 0, durationSec);
        const b = clamp(b0, 0, durationSec);

        // ✅ Timeline-absolute positioning
        const x = a * pxPerSec;
        const w = Math.max(1, (b - a) * pxPerSec);

        const isSelected = selectedRangeId != null && r.id === selectedRangeId;

        // Lane clip rect (Option 2)
        const lane = r.trackId ? laneRects[r.trackId] : undefined;

        // ✅ Hard rule: lane-scoped envelope bands never render without a lane rect.
        if (isEnvelope && r.trackId && !lane) return null;

        /* ------------------------------ */
        /* Phase strip (thin band)         */
        /* ------------------------------ */
        if (isPhase) {
          const p = parsePhaseLabel(r.label) ?? "setup";
          const pc = phaseColors(p);
          const canEdit = Boolean(dispatch);

          return (
            <div
              key={r.id}
              className="absolute"
              style={{
                left: x,
                width: w,
                top: PHASE_STRIP_TOP,
                height: PHASE_STRIP_H,
              }}
            >
              <div
                aria-hidden
                className="absolute inset-0 rounded-md"
                style={{
                  pointerEvents: "none",
                  background: pc.fill,
                  boxShadow: isSelected
                    ? isDark
                      ? "0 0 0 1px rgba(255,255,255,0.18) inset"
                      : "0 0 0 1px rgba(2,6,23,0.14) inset"
                    : "0 0 0 1px rgba(255,255,255,0.08) inset",
                }}
              />
              <div
                aria-hidden
                className="absolute left-0 right-0"
                style={{ top: 0, height: 1, background: pc.stroke, opacity: 0.75, pointerEvents: "none" }}
              />
              <div
                aria-hidden
                className="absolute left-0 right-0"
                style={{ bottom: 0, height: 1, background: pc.stroke, opacity: 0.55, pointerEvents: "none" }}
              />

              {canEdit ? (
                <div className="pointer-events-auto absolute inset-0">
                  <div
                    role="button"
                    aria-label={`Phase ${p}`}
                    title="Drag to move; edges to resize"
                    className="absolute inset-0 rounded-md"
                    style={{ cursor: "grab", background: "transparent" }}
                    onPointerDown={(e) => beginDrag(e, r, "move")}
                    onClick={() => onSelect?.(r.id)}
                  />

                  <div
                    aria-hidden
                    className="absolute inset-y-0 left-0"
                    style={{ width: 10, cursor: "ew-resize" }}
                    onPointerDown={(e) => beginDrag(e, r, "resizeStart")}
                    title="Resize start"
                  />
                  <div
                    aria-hidden
                    className="absolute inset-y-0 right-0"
                    style={{ width: 10, cursor: "ew-resize" }}
                    onPointerDown={(e) => beginDrag(e, r, "resizeEnd")}
                    title="Resize end"
                  />

                  <div className="absolute inset-y-0 left-2 flex items-center" style={{ pointerEvents: "none" }}>
                    <div
                      className="rounded-full px-2 py-0.5 text-[11px] border border-white/10 bg-black/25 text-white/90 backdrop-blur max-w-[180px] truncate"
                      title={r.label ?? `phase:${p}`}
                    >
                      {r.label ?? `phase:${p}`}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="pointer-events-auto absolute inset-0" onClick={() => onSelect?.(r.id)} />
              )}
            </div>
          );
        }

        /* ------------------------------ */
        /* Envelope (lane-scoped)          */
        /* ------------------------------ */

        const laneTop = lane ? clamp(lane.top, 0, 1e9) : 0;
        const laneHeight = lane ? Math.max(1, lane.height) : undefined;

        const top = isEnvelope && laneHeight != null ? laneTop : 0;
        const heightStyle = isEnvelope && laneHeight != null ? laneHeight : "100%";

        const c = colorFor(kindNorm);

        const envelopeFill =
          kindNorm === "attention"
            ? "rgba(250,204,21,0.12)"
            : kindNorm === "emotion"
              ? "rgba(168,85,247,0.12)"
              : kindNorm === "safety"
                ? "rgba(34,197,94,0.12)"
                : kindNorm === "overload"
                  ? "rgba(245,158,11,0.14)"
                  : c.fill;

        const showChip = Boolean(onSelect) && !isEnvelope;
        const chipText = r.label ?? r.tag ?? kindNorm ?? "range";
        const canEditEnvelope = Boolean(dispatch) && isEnvelope;

        return (
          <div key={r.id} className="absolute" style={{ left: x, width: w, top, height: heightStyle as any }}>
            <div
              className="absolute inset-0 rounded-md"
              style={{
                pointerEvents: "none",
                background: isEnvelope ? envelopeFill : c.fill,
                boxShadow: isSelected
                  ? isDark
                    ? "0 0 0 1px rgba(255,255,255,0.18) inset"
                    : "0 0 0 1px rgba(2,6,23,0.14) inset"
                  : undefined,
              }}
            />

            {isEnvelope ? (
              <>
                <div
                  aria-hidden
                  className="absolute left-0 right-0"
                  style={{
                    top: 0,
                    height: 1,
                    background: c.stroke,
                    opacity: isDark ? 0.55 : 0.35,
                    pointerEvents: "none",
                  }}
                />
                <div
                  aria-hidden
                  className="absolute left-0 right-0"
                  style={{
                    bottom: 0,
                    height: 1,
                    background: c.stroke,
                    opacity: isDark ? 0.55 : 0.35,
                    pointerEvents: "none",
                  }}
                />

                {canEditEnvelope && (
                  <div className="pointer-events-auto absolute inset-0">
                    <div
                      role="button"
                      aria-label={`Envelope ${kindNorm}`}
                      title="Drag to move; edges to resize"
                      className="absolute inset-0 rounded-md"
                      style={{
                        cursor: "grab",
                        boxShadow: isSelected
                          ? "0 0 0 1px rgba(255,255,255,0.18) inset"
                          : "0 0 0 1px rgba(255,255,255,0.10) inset",
                      }}
                      onPointerDown={(e) => beginDrag(e, r, "move")}
                      onDoubleClick={() => onSelect?.(r.id)}
                    />

                    <div
                      aria-hidden
                      className="absolute inset-y-0 left-0"
                      style={{ width: 10, cursor: "ew-resize" }}
                      onPointerDown={(e) => beginDrag(e, r, "resizeStart")}
                      title="Resize start"
                    >
                      <div
                        className="absolute inset-y-1 left-1 rounded"
                        style={{ width: 2, background: "rgba(255,255,255,0.20)" }}
                      />
                    </div>

                    <div
                      aria-hidden
                      className="absolute inset-y-0 right-0"
                      style={{ width: 10, cursor: "ew-resize" }}
                      onPointerDown={(e) => beginDrag(e, r, "resizeEnd")}
                      title="Resize end"
                    >
                      <div
                        className="absolute inset-y-1 right-1 rounded"
                        style={{ width: 2, background: "rgba(255,255,255,0.20)" }}
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div
                  aria-hidden
                  className="absolute left-0 right-0"
                  style={{
                    top: "50%",
                    height: 1,
                    transform: "translateY(-0.5px)",
                    background: c.stroke,
                    opacity: isDark ? 0.85 : 0.7,
                    pointerEvents: "none",
                  }}
                />
                <div
                  aria-hidden
                  className="absolute left-0 right-0"
                  style={{
                    top: "50%",
                    height: 1,
                    transform: "translateY(0.5px)",
                    background: innerLine,
                    opacity: isSelected ? 0.75 : isDark ? 0.55 : 0.35,
                    pointerEvents: "none",
                  }}
                />
              </>
            )}

            {showChip && (
              <button
                type="button"
                className="pointer-events-auto absolute -top-2 right-1 rounded-full px-2 py-0.5 text-[11px]
                           border border-white/10 bg-black/30 text-white backdrop-blur
                           hover:bg-black/40
                           dark:bg-black/35 dark:hover:bg-black/45
                           max-w-[80%] truncate"
                onClick={() => onSelect?.(r.id)}
                title={chipText}
              >
                {chipText}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
